/**
 * i18n runtime — docs/I18N_PLAN.md. Typed t() with {var} interpolation,
 * language = user's saved choice → device locale → English. Missing keys
 * always fall back to English (never blanks). Switching language re-renders
 * live via context; no restart needed. OTA-safe (pure JS).
 */
import * as Localization from 'expo-localization';
import React from 'react';

import { useSession } from '@/lib/session';
import { en, type Dict, type DictKey } from './en';
import { hi } from './hi';
import { mr } from './mr';

const DICTS: Record<string, Dict> = { hi, mr };
export const SUPPORTED = ['en', 'hi', 'mr'] as const;

function normalize(code?: string | null): string | null {
  const c = (code ?? '').toLowerCase().slice(0, 2);
  return (SUPPORTED as readonly string[]).includes(c) ? c : null;
}

function deviceLang(): string | null {
  try {
    return normalize(Localization.getLocales()[0]?.languageCode);
  } catch {
    return null;
  }
}

export type TFunc = (key: DictKey, vars?: Record<string, string | number>) => string;

/** Pure translate for a given language — no hook. Used by the provider's t()
 * and by the module-level `translate()` for non-React callers (date formatters). */
function translateFor(lang: string, key: DictKey, vars?: Record<string, string | number>): string {
  let s: string = (lang !== 'en' ? DICTS[lang]?.[key] : undefined) ?? en[key];
  if (vars) {
    for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
  }
  return s;
}

// The active language, kept in sync by the provider so pure functions (e.g.
// timeAgo) can localise without a hook. Defaults to 'en' until the provider mounts.
const active = { lang: 'en' };
/** Non-React translate for the current active language (date/relative-time formatters). */
export function translate(key: DictKey, vars?: Record<string, string | number>): string {
  return translateFor(active.lang, key, vars);
}

/* ---- localised month / day names (for the shared date formatters) ---- */
const MONTH_SHORT: Record<string, string[]> = {
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  hi: ['जन', 'फ़र', 'मार्च', 'अप्रै', 'मई', 'जून', 'जुला', 'अग', 'सित', 'अक्तू', 'नव', 'दिस'],
  mr: ['जाने', 'फेब्रु', 'मार्च', 'एप्रि', 'मे', 'जून', 'जुलै', 'ऑग', 'सप्टें', 'ऑक्टो', 'नोव्हें', 'डिसें'],
};
const MONTH_LONG: Record<string, string[]> = {
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  hi: ['जनवरी', 'फ़रवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्तूबर', 'नवंबर', 'दिसंबर'],
  mr: ['जानेवारी', 'फेब्रुवारी', 'मार्च', 'एप्रिल', 'मे', 'जून', 'जुलै', 'ऑगस्ट', 'सप्टेंबर', 'ऑक्टोबर', 'नोव्हेंबर', 'डिसेंबर'],
};
const DAY_SHORT: Record<string, string[]> = {
  en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  hi: ['रवि', 'सोम', 'मंगल', 'बुध', 'गुरु', 'शुक्र', 'शनि'],
  mr: ['रवि', 'सोम', 'मंगळ', 'बुध', 'गुरु', 'शुक्र', 'शनि'],
};
const DAY_LONG: Record<string, string[]> = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  hi: ['रविवार', 'सोमवार', 'मंगलवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'],
  mr: ['रविवार', 'सोमवार', 'मंगळवार', 'बुधवार', 'गुरुवार', 'शुक्रवार', 'शनिवार'],
};
const pickName = (table: Record<string, string[]>, i: number): string =>
  (table[active.lang] ?? table.en)[i] ?? '';
/** Localised month name for month index 0–11 (abbreviated). */
export const monthShort = (i: number): string => pickName(MONTH_SHORT, i);
/** Localised full month name for month index 0–11. */
export const monthLong = (i: number): string => pickName(MONTH_LONG, i);
/** Localised weekday name for day index 0–6 (abbreviated, Sun=0). */
export const dayShort = (i: number): string => pickName(DAY_SHORT, i);
/** Localised full weekday name for day index 0–6 (Sun=0). */
export const dayLong = (i: number): string => pickName(DAY_LONG, i);

const I18nContext = React.createContext<{ lang: string; t: TFunc }>({
  lang: 'en',
  t: (key) => en[key],
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const { user } = useSession();
  const lang = normalize(user?.language) ?? deviceLang() ?? 'en';
  // Mirror the language to the module singleton for the few NON-hook callers
  // that can't take t() — date formatters (monthShort etc.) and parse-time
  // helpers (notifyLabel). Those run off fetched data, by which point the
  // language has settled, so an effect (a render late) is fine. Anything that
  // renders a resolver on STATIC data must take the reactive t() instead (see
  // catLabel-style resolvers in features/community/lib) so it's never stale.
  React.useEffect(() => { active.lang = lang; }, [lang]);

  const t = React.useCallback<TFunc>((key, vars) => translateFor(lang, key, vars), [lang]);

  const value = React.useMemo(() => ({ lang, t }), [lang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** The one hook screens use: `const { t } = useT();` */
export function useT() {
  return React.useContext(I18nContext);
}
